import { useState, useEffect } from "react";
import { MessageDisplayType, ErrorStackDataType } from "@preveil-api";
import { FSMessage, FSStatus } from "src/common/keys/protos/collections_pb";
import {
  Account, useAppDispatch, PermissionSetType, CollectionFilesyncAPI, DriveErrorMessages, MessageHandlerDisplayType, useSendRequestMutation, Message, getEnumKey, NodeIdentifier,
  ErrorStackItem, Snapshot, DriveLimits
} from "src/common";
import { uiActions } from "src/store";

// NOTE: Concurrent per-file block fetches during snapshot build.
const FETCH_BLOCKS_CONCURRENCY = 8;

// Description: Return the entire state of the collection in a single response message.
// https://github.com/PreVeil/core/blob/dev/collection_server/docs/filesync.md#snapshot
// NOTE: onProgress fires as pages and block batches come in so the caller can move the bar during the fetch.
// Page/block totals aren't known up front, so it's just forward movement, not a real percentage.
export function useFetchSnapshot(current_account: Account, node_identifiers?: NodeIdentifier, permissions?: PermissionSetType, onProgress?: () => void) {
  const [snapshot, setSnapshot] = useState<FSMessage.Snapshot | undefined>();
  const [error, setError] = useState<boolean>(false);
  const [sendRequest] = useSendRequestMutation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    (!!permissions && !!node_identifiers) ?
      fetchSnapshot(node_identifiers) :
      // NOTE: else destroy previously fetched 
      (!!snapshot && setSnapshot(undefined));
  }, [permissions, node_identifiers]);

  // Description: Bulk update permissions
  async function fetchSnapshot(_node_identifiers: NodeIdentifier, seq: number = 0, subseq: number = 0, prev_snapshot: FSMessage.Snapshot = new FSMessage.Snapshot()) {
    const drive_request = !!permissions ?
      await CollectionFilesyncAPI.fetchSnapshot(current_account, permissions, _node_identifiers.collection_id, _node_identifiers.id, seq, subseq, DriveLimits.DRIVE_SHARE_SNAPSHOT_LIMIT) : null;
    async function snapshotCallback(new_snapshot: FSMessage.Snapshot, has_more: boolean) {
      has_more ? fetchSnapshot(_node_identifiers, new_snapshot.getCursorSeq(), new_snapshot.getCursorSubseq(), new_snapshot) : handleSuccess(new_snapshot);
    }
    async function callback(message: FSMessage) {
      if (message.getStatus() === FSStatus.OK) {
        const response_snapshot = message.getSnapshot();
        if (!!response_snapshot) {
          // NOTE: Recursive and cumulative
          const new_seq = response_snapshot.getCursorSeq() || seq;
          const new_subseq = response_snapshot.getCursorSubseq() || subseq;
          buildSnapshot(_node_identifiers, new_seq, new_subseq, response_snapshot, prev_snapshot, snapshotCallback);
        } else {
          handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_snapshot }, message);
        }
      } else {
        handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_snapshot }, message);
      }
    }

    if (!!drive_request) {
      drive_request.setCallback(callback);
      sendRequest(drive_request);
    } else {
      handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_updating_node_permissions });
    }
  }


  // Description: Get Snapshot ready for sharing — fetch any omitted block lists in bounded-parallel batches, then assemble.
  async function buildSnapshot(_node_identifiers: NodeIdentifier, seq: number, subseq: number, response_snapshot: FSMessage.Snapshot, prev_snapshot: FSMessage.Snapshot, callback: (snapshot: FSMessage.Snapshot, has_more: boolean) => void) {
    const files_needing_blocks = response_snapshot.getFilesList().filter(file => file.getDataOmitted());
    for (let i = 0; i < files_needing_blocks.length; i += FETCH_BLOCKS_CONCURRENCY) {
      const batch = files_needing_blocks.slice(i, i + FETCH_BLOCKS_CONCURRENCY);
      // NOTE: Resolve to true on success and false on any server-response failure
      const results = await Promise.all(batch.map(file => new Promise<boolean>(resolve => {
        fetchBlocks(_node_identifiers, file, (blocks) => {
          file.setDataList(blocks);
          file.setDataOmitted(false);
          resolve(true);
        }, () => resolve(false)).catch(() => resolve(false));
      })));
      // NOTE: Bail before assembling if any block fetch in this batch failed.
      if (results.includes(false)) {
        handlePageErrorMessage(DriveErrorMessages.error_fetching_snapshot, { stack_message: DriveErrorMessages.error_fetching_snapshot });
        return;
      }
      // NOTE: Move the progress after each batch so the bar keeps moving while blocks download.
      onProgress?.();
    }
    const new_snapshot = Snapshot.buildSnapshot(seq, subseq, response_snapshot, prev_snapshot);
    // NOTE: Move the progress once per page too, so pages with all-inline files (no batches above) still move the bar.
    onProgress?.();
    callback(new_snapshot, response_snapshot.getHasMore() || false);
  }

  // NOTE: onError fires on every failure path (bad status, missing file_blocks, missing request) so the caller
  // can settle its pending promise.
  async function fetchBlocks(_node_identifiers: NodeIdentifier, file: FSMessage.File, callback: (blocks: FSMessage.Block[]) => void, onError?: () => void, previous_blocks: FSMessage.Block[] = [], offset: number = 0) {
    const id = file.getId_asB64();
    const collection_id = _node_identifiers.collection_id;
    const drive_request = !!permissions ? await CollectionFilesyncAPI.fetchFileBlocks(current_account, permissions, collection_id, id, 100, offset) : null;
    async function fetchBlocksCallback(message: FSMessage) {
      if (message.getStatus() === FSStatus.OK) {
        const file_blocks = message.getFileBlocks();
        if (!!file_blocks) {
          const blocks = previous_blocks.concat(file_blocks.getBlocksList());
          if (file_blocks.getHasMore()) {
            fetchBlocks(_node_identifiers, file, callback, onError, blocks, offset + Number(file_blocks.getBlocksList().length));
          } else {
            callback(blocks);
          }
        } else {
          // NOTE: Log only — buildSnapshot surfaces a single toast for the whole batch on bail.
          handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_snapshot }, message, MessageHandlerDisplayType.logger);
          onError?.();
        }
      } else {
        handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_snapshot }, message, MessageHandlerDisplayType.logger);
        onError?.();
      }
    }
    if (!!drive_request) {
      drive_request.setCallback(fetchBlocksCallback);
      sendRequest(drive_request);
    } else {
      handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_snapshot }, null, MessageHandlerDisplayType.logger);
      onError?.();
    }
  }

  // Description: Handle success for fetching snapshot
  function handleSuccess(_snapshot: FSMessage.Snapshot) {
    setSnapshot(_snapshot);
    setError(false);
  }

  // Description: Handle error message and send error to store
  function handlePageErrorMessage(message: string, stack_data?: ErrorStackDataType, fsmessage?: FSMessage | null,
    display_type: MessageDisplayType = MessageHandlerDisplayType.toastr) {
    setError(true);
    const status = !!fsmessage?.getStatus() ? getEnumKey(FSStatus, Number(fsmessage.getStatus())) : "empty";
    const stack = new ErrorStackItem("[useFetchSnapshot Hook]", fsmessage?.getErrorMessage() || message, status || "error", stack_data).info;
    dispatch(uiActions.handleRequestErrors(new Message(message, display_type), stack));
    setSnapshot(undefined);
  }

  return {
    snapshot,
    error
  };
}